/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-param, jsdoc/require-returns */
import { ReactElement, useContext, useMemo } from 'react';
import LineChart from '../LineChart';
import { ThemeContext } from '../../context/theme.context';

export type LivePoint = { x: number; y: number };
export type LiveSeries = { label: string; color: string; points: LivePoint[] };

type Props = {
  series: LiveSeries[];
  /** Visible window: [now - windowMs, now]. */
  windowMs: number;
  /** Bumped by the page whenever new data has been appended or time moved on. */
  version: number;
  unit?: string;
  height?: string;
};

/**
 * Live adapter over the dashboard's shared {@link LineChart}, so these graphs
 * look and behave (crosshair, click-to-pin tooltips) like every other line
 * chart in the app. The react-chartjs-2 wrapper updates the chart in place when
 * data changes; animation is off so each update is a cheap redraw. The axis
 * is a linear time scale rather than "timeseries", because live points are
 * irregularly spaced and must sit at their real times.
 */
export default function LiveLineChart({
  series,
  windowMs,
  version,
  unit,
  height = 'h-56'
}: Props): ReactElement {
  const { isDarkTheme } = useContext(ThemeContext);
  const tickColor = isDarkTheme ? '#98A2B3' : '#667085';
  const gridColor = isDarkTheme ? 'rgba(152,162,179,0.12)' : '#F2F4F7';

  const data = useMemo(
    () => ({
      datasets: series.map((s) => ({
        label: s.label,
        data: s.points,
        borderColor: s.color,
        backgroundColor: s.color,
        borderWidth: 1.75,
        pointRadius: 0,
        pointHitRadius: 6,
        tension: 0
      }))
    }),
    // `version` signals that the page's ref-held points changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, version]
  );

  const options = useMemo(() => {
    const now = Date.now();
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      spanGaps: 60000,
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      plugins: {
        legend: {
          display: series.length > 1,
          position: 'bottom',
          labels: { color: tickColor, boxWidth: 10, boxHeight: 2 }
        },
        title: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) =>
              `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(3).replace(/\.?0+$/, '')}${
                unit ? ` ${unit}` : ''
              }`
          }
        }
      },
      scales: {
        x: {
          type: 'time',
          // Pinned to the live window so the axis scrolls even with no data.
          min: now - windowMs,
          max: now,
          time: { tooltipFormat: 'HH:mm:ss', displayFormats: { second: 'HH:mm:ss', minute: 'HH:mm' } },
          grid: { display: false },
          ticks: { color: tickColor, maxTicksLimit: 6, maxRotation: 0 }
        },
        y: {
          beginAtZero: false,
          grid: { display: true, color: gridColor },
          ticks: { color: tickColor, maxTicksLimit: 5 }
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowMs, version, unit, series.length, tickColor, gridColor]);

  return (
    <div className={`relative ${height} w-full`}>
      <LineChart data={data} options={options} />
    </div>
  );
}
